import { type TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { generatePath, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { PrefetchLink } from '@sorare/routing';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandXS, DisplayXS } from '@sorare/core/src/atoms/typography';
import { FOOTBALL_PLAY_PRO_ONBOARDING_LEADERBOARD_CHOOSE_TEAMS } from '@sorare/core/src/constants/__generated__/routes';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useQueryString } from '@sorare/core/src/hooks/useQueryString';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CompetitionTile } from 'components/onboarding/CompetitionTile';
import { ExpertBanner } from 'components/onboarding/ExpertBanner';
import { MarketGuidanceLayout } from 'components/onboarding/MarketGuidanceLayout';
import { PromoBanner } from 'components/onboarding/PromoBanner';

import type {
  ChooseCompetitionQuery,
  ChooseCompetitionQueryVariables,
  UpdateOnboardingLeaderboard,
  UpdateOnboardingLeaderboardVariables,
} from './__generated__/page.graphql';

const Competitions = styled(Vertical)`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  align-items: stretch;

  @media ${tabletAndAbove} {
    grid-template-columns: repeat(4, 1fr);
  }
`;
const PromoBannerWrapper = styled(Vertical).attrs({ center: true })`
  position: sticky;
  bottom: var(--double-unit);

  @media ${tabletAndAbove} {
    position: static;
  }
`;

type ChooseCompetitionOnboardingLeaderboard = NonNullable<
  NonNullable<ChooseCompetitionQuery['currentUser']>['anyUserSportProfile']
>['onboardingLeaderboards'][number];

const partitionLeaderboards = (
  onboardingLeaderboards: ChooseCompetitionOnboardingLeaderboard[]
) => {
  const mainLeaderboards: ChooseCompetitionOnboardingLeaderboard[] = [];
  const otherLeaderboards: ChooseCompetitionOnboardingLeaderboard[] = [];

  onboardingLeaderboards.forEach(ol => {
    if ((ol.playableLeaderboard?.so5League.competitions.length || 0) > 1) {
      otherLeaderboards.push(ol);
    } else {
      mainLeaderboards.push(ol);
    }
  });

  return { mainLeaderboards, otherLeaderboards };
};
const sortLeaderboardFunction = (
  a: ChooseCompetitionOnboardingLeaderboard,
  b: ChooseCompetitionOnboardingLeaderboard
) => {
  if (
    (a.playableLeaderboard && b.playableLeaderboard) ||
    (!a.playableLeaderboard && !b.playableLeaderboard)
  ) {
    return 0;
  }
  if (a.playableLeaderboard) {
    return -1;
  }
  return 1;
};

const CHOOSE_COMPETITION_QUERY = gql`
  query ChooseCompetitionQuery($rarity: Rarity) {
    currentUser {
      slug
      onboardingConversionCredit(sport: FOOTBALL) {
        id
        ...PromoBanner_conversionCredit
      }
      anyUserSportProfile(sport: FOOTBALL) {
        id
        onboardingLeaderboards(rarity: $rarity) {
          id
          playableLeaderboard {
            slug
            id
            so5League {
              slug
              competitions {
                slug
              }
            }
          }
          ...CompetitionTile_onboardingLeaderboard
        }
      }
    }
  }
  ${CompetitionTile.fragments.onboardingLeaderboard}
  ${PromoBanner.fragments.conversionCredit}
` as TypedDocumentNode<ChooseCompetitionQuery, ChooseCompetitionQueryVariables>;

const UPDATE_ONBOARDING_LEADERBOARD = gql`
  mutation UpdateOnboardingLeaderboard(
    $input: updateOnboardingSo5LeaderboardInput!
  ) {
    updateOnboardingSo5Leaderboard(input: $input) {
      errors {
        code
        message
      }
    }
  }
` as TypedDocumentNode<
  UpdateOnboardingLeaderboard,
  UpdateOnboardingLeaderboardVariables
>;

const ChooseCompetitionPage = () => {
  const rarity = useQueryString('rarity') || 'limited';

  const [update] = useMutation(UPDATE_ONBOARDING_LEADERBOARD, {
    showErrorsWithSnackNotification: true,
  });
  const navigate = useNavigate();
  const { data } = useQuery(CHOOSE_COMPETITION_QUERY, {
    variables: { rarity: (rarity || 'limited') as Rarity },
  });
  const track = useEvents();
  const isLaptopAndAbove = useIsLaptop();
  const { currentUser } = data || {};
  const { onboardingLeaderboards = [] } =
    currentUser?.anyUserSportProfile || {};

  const { mainLeaderboards, otherLeaderboards } = partitionLeaderboards(
    onboardingLeaderboards
  );
  const sortedMainLeaderboards = [...mainLeaderboards].sort(
    sortLeaderboardFunction
  );
  const sortedOtherLeaderboards = [...otherLeaderboards].sort(
    sortLeaderboardFunction
  );

  const onLeaderboardClick = ({
    so5LeaderboardId,
    so5LeaderboardSlug,
    displayName,
  }: {
    so5LeaderboardId: string;
    so5LeaderboardSlug: string;
    displayName: string;
  }) => {
    track('Select Competition In Guidance', {
      competition: displayName,
    });
    update({
      variables: {
        input: {
          onboardingSo5LeaderboardId: so5LeaderboardId,
        },
      },
    });
    navigate(
      `${generatePath(FOOTBALL_PLAY_PRO_ONBOARDING_LEADERBOARD_CHOOSE_TEAMS, {
        leaderboard: so5LeaderboardSlug,
      })}?rarity=${rarity}`
    );
  };

  return (
    <MarketGuidanceLayout progress={20}>
      <PrefetchLink
        to={FOOTBALL_PLAY_PRO_ONBOARDING_LEADERBOARD_CHOOSE_TEAMS}
      />
      <Vertical gap={3}>
        <Vertical gap={2.5}>
          <StarBall width={48} height={48} />
          <DisplayXS>
            <FormattedMessage
              id="ChooseCompetitionPage.explanation2"
              defaultMessage="Pick a competition to scout players from first."
            />
          </DisplayXS>
        </Vertical>
        <Competitions>
          {sortedMainLeaderboards.map(onboardingLeaderboard => {
            return (
              <CompetitionTile
                key={onboardingLeaderboard.id}
                onboardingLeaderboard={onboardingLeaderboard}
                isMainLeaderboard
                onClick={onLeaderboardClick}
              />
            );
          })}
        </Competitions>
        {sortedOtherLeaderboards.length > 0 && (
          <Vertical gap={2}>
            <BrandXS>
              <FormattedMessage
                id="ChooseCompetitionPage.otherCompetitions"
                defaultMessage="Other competitions"
              />
            </BrandXS>
            <Vertical gap={1.5}>
              {sortedOtherLeaderboards.map(onboardingLeaderboard => {
                return (
                  <CompetitionTile
                    key={onboardingLeaderboard.id}
                    onboardingLeaderboard={onboardingLeaderboard}
                    isMainLeaderboard={false}
                    onClick={onLeaderboardClick}
                  />
                );
              })}
            </Vertical>
          </Vertical>
        )}
        <ExpertBanner />
        <PromoBannerWrapper>
          <PromoBanner
            conversionCredit={currentUser?.onboardingConversionCredit}
            leftAligned={isLaptopAndAbove}
          />
        </PromoBannerWrapper>
      </Vertical>
    </MarketGuidanceLayout>
  );
};
export default ChooseCompetitionPage;
